import React, { useMemo } from "react";
import { ProfileBuilderContext } from "modules/profileBuilder/services";
import { useContext } from "react";
import styled, { css } from "styled-components";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import ActionMenu from "components/common/ActionMenu";
import {
  borderDefault,
  elevationSurfaceSubtlest,
  lightNeutralNeutral1000,
  red,
  backgroundBrandBold,
  backgroundAccentNeutralSubtle,
  textSubtle,
  textInverse,
  lightCyan,
  primaryBlue,
} from "utils/constants/colors";
import { Tooltip } from "antd";
import Button from "components/ui/Button";
import { Trans, useTranslation } from "react-i18next";
import EmptyResults from "components/ui/EmptyResults";
import DocLink from "components/common/DocLink";
import { faBracketsCurly } from "@fortawesome/pro-regular-svg-icons";
import Icon from "components/ui/Icon";
import Clipboard from "components/common/Clipboard";
import Alert from "components/common/Alert";
import { useSelector } from "react-redux";
import { faGripDotsVertical } from "@fortawesome/pro-light-svg-icons";
import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons";
import InfoCard from "components/ui/InfoCard/InfoCard";
import Ellipsis from "components/common/Ellipsis";

const VariableWrap = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border-radius: 4px;
  border: 1px solid ${borderDefault};
  background-color: ${elevationSurfaceSubtlest};
  font-size: 12px;
  line-height: 16px;
`;

const VariableUsage = styled.div`
  color: ${textSubtle};
  background-color: ${backgroundAccentNeutralSubtle};
  padding: 2px 6px;
  line-height: 16px;
  border-radius: 10px;
  ${(props) =>
    props.inUse &&
    css`
      background-color: ${backgroundBrandBold};
      color: ${textInverse};
    `}
`;

const VariablesWrap = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  margin-bottom: 34px;

  ${Button} {
    width: 100%;
  }
`;

const DragAndDropArea = styled.div`
  flex-grow: 1;
  overflow-y: auto;
  margin: 8px 0;

  > * + * {
    margin-top: 8px;
  }
`;

const VariableName = styled.div`
  display: flex;
  font-size: 12px;
  font-weight: 600;
  margin-right: 5px;
  color: ${lightNeutralNeutral1000};
  flex-grow: 1;
  overflow: hidden;
`;

const VariablePartWrap = styled.div`
  display: flex;
  align-items: center;
  flex-grow: 1;
`;

const NameWrap = styled.div`
  display: flex;
  align-items: center;
  flex-grow: 1;
  overflow: hidden;

  > div {
    max-width: 100%;
  }
`;

const InfoCardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
`;

const StyledAlert = styled(Alert)`
  margin: -24px;
  padding: 8px 16px;
  border: none;
  align-items: center;
`;

const StyledWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const Variable = React.memo(function VariablePrimitive({
  variable,
  variableTotalUsage,
  draggable,
  inUse,
  editVariable,
  deleteVariable,
  actions,
}) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: variable.guid });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  function renderVariableName() {
    const variableName = (
      <VariableName>
        {"{{.spectro.var."}
        <Ellipsis>{variable.name}</Ellipsis>
        {"}}"}
      </VariableName>
    );
    if (draggable) {
      return variableName;
    }
    return (
      <Clipboard text={`{{.spectro.var.${variable.name}}}`}>
        <Tooltip
          title={
            !draggable ? (
              <Trans>
                Display Name: {variable.displayName}
                <p>Description: {variable.description || "-"}</p>
              </Trans>
            ) : null
          }
        >
          {variableName}
        </Tooltip>
      </Clipboard>
    );
  }

  function renderUsageBreakdown() {
    if (!variableTotalUsage[variable.name]) {
      return null;
    }
    return (
      <>
        {t("Usage breakdown")}
        {(variable.usedIn || []).map((usage) => (
          <div key={usage.name}>
            {usage.parentName ? `${usage.parentName}/` : ""}
            {usage.name}: {usage.occurrences}
          </div>
        ))}
      </>
    );
  }

  function renderActions() {
    if (draggable) {
      return null;
    }

    if (actions) {
      return actions(variable);
    }
    return (
      <ActionMenu
        options={[
          {
            label: inUse ? t("View") : t("Edit"),
            onClick: () => editVariable(variable),
          },
          inUse || variableTotalUsage[variable.name]
            ? null
            : {
                label: t("Delete"),
                color: red,
                onClick: () => deleteVariable(variable),
              },
        ].filter(Boolean)}
      />
    );
  }

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <VariableWrap>
        <NameWrap>
          {draggable ? (
            <div {...listeners}>
              <Icon
                awesome={faGripDotsVertical}
                style={{ cursor: "grab", marginRight: "8px" }}
              />
            </div>
          ) : null}
          {renderVariableName()}
        </NameWrap>
        <VariablePartWrap>
          <Tooltip title={renderUsageBreakdown()}>
            <VariableUsage inUse={!!variableTotalUsage[variable.name]}>
              {!!variableTotalUsage[variable.name]
                ? `${variableTotalUsage[variable.name]} layers`
                : "unused"}
            </VariableUsage>
          </Tooltip>
        </VariablePartWrap>
        {renderActions()}
      </VariableWrap>
    </div>
  );
});

export const InfoBanner = () => {
  const { t } = useTranslation();
  return (
    <StyledAlert
      description={t(
        "Variables are profile values and are available across packs"
      )}
      type="info"
      showIcon
    />
  );
};

export const EmptyVariables = ({ actions = null }) => {
  const { t } = useTranslation();
  return (
    <>
      <InfoCard
        description={t(
          "Cluster profile variables enable you to create placeholders for parameters in profile layer configurations, which you can then populate for individual clusters during deployment."
        )}
        icon={
          <Icon
            awesome={faCircleInfo}
            style={{ color: primaryBlue, fontSize: "24px" }}
          />
        }
        theme={{ backgroundColor: lightCyan }}
      >
        <DocLink
          location="profile-variables"
          text={t("What are profile variables?")}
        />
      </InfoCard>
      <EmptyResults description={<p>{t("No profile variables yet")}</p>}>
        {actions}
      </EmptyResults>
    </>
  );
};

export function Variables({
  inUse,
  variablesOrderMode,
  variables,
  createVariable,
  updateVariablesOrder,
  confirmVariablesOrder,
  editVariable = () => {},
  deleteVariable = () => {},
  allowCreate = true,
  actions = null,
}) {
  const { t } = useTranslation();
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  let inUseWarning = null;

  const variableTotalUsage = useMemo(() => {
    return variables.reduce((acc, variable) => {
      acc[variable.name] = variable.usedIn?.reduce(
        (usageAcc, { occurrences }) => {
          return usageAcc + occurrences;
        },
        0
      );
      return acc;
    }, {});
  }, [variables]);
  const variableGuids = variables.map((variable) => variable.guid);

  let createButton = allowCreate ? (
    <Button secondary data-qa="create-variable" onClick={createVariable}>
      <Icon awesome={faBracketsCurly} />
      {t("Create variable")}
    </Button>
  ) : null;

  if (inUse) {
    inUseWarning = (
      <Alert type="warning">
        <h4>Profile is in use</h4>
        <p>
          Variables cannot be removed, created or edited because this profile is
          used in cluster(s)/template(s)
        </p>
        <h4>What can I do?</h4>
        <p>
          To edit variables under this profile, you can create a new version for
          it. This allows you to modify profile variables without affecting the
          current configuration.
        </p>
        <DocLink
          text={t("How to version a cluster profile")}
          location="cluster-profile-versioning"
        />
      </Alert>
    );
    createButton = null;
  }

  if (!variables.length) {
    if (inUse) {
      return inUseWarning;
    }

    return (
      <InfoCardWrapper>
        <div>
          <EmptyVariables actions={createButton} />
        </div>
        <InfoBanner />
      </InfoCardWrapper>
    );
  }

  function handleDragEnd(event) {
    const { active, over } = event;
    if (active.id !== over.id) {
      const oldIndex = variables.findIndex(
        (variable) => variable.guid === active.id
      );
      const newIndex = variables.findIndex(
        (variable) => variable.guid === over.id
      );
      updateVariablesOrder({
        oldIndex,
        newIndex,
      });
    }
  }

  let confirmOrderButton = null;
  if (variablesOrderMode) {
    createButton = null;
    confirmOrderButton = (
      <Button secondary data-qa="confirm-order" onClick={confirmVariablesOrder}>
        {t("Confirm order")}
      </Button>
    );
  }

  return (
    <StyledWrapper>
      <VariablesWrap>
        {createButton}
        {inUseWarning}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={variableGuids}
            strategy={verticalListSortingStrategy}
          >
            <DragAndDropArea>
              {(variables || []).map((variable) => (
                <Variable
                  key={variable.guid}
                  variable={variable}
                  variableTotalUsage={variableTotalUsage}
                  draggable={variablesOrderMode}
                  inUse={inUse}
                  editVariable={() => editVariable(variable)}
                  deleteVariable={deleteVariable}
                  actions={actions}
                />
              ))}
            </DragAndDropArea>
          </SortableContext>
        </DndContext>
        {confirmOrderButton}
      </VariablesWrap>
      <InfoBanner />
    </StyledWrapper>
  );
}

export default function ProfileVariables() {
  const { state, selectors, actions } = useContext(ProfileBuilderContext);
  const variables = useSelector(selectors.getVariables);

  return (
    <Variables
      inUse={state.isClusterProfileUsed || state.isProfileAssociatedToTemplate}
      variablesOrderMode={state.variablesOrderMode}
      variables={variables}
      createVariable={actions.createVariable}
      updateVariablesOrder={actions.updateVariablesOrder}
      confirmVariablesOrder={actions.confirmVariablesOrder}
      editVariable={actions.editVariable}
      deleteVariable={actions.deleteVariable}
    />
  );
}
